import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type DepartmentItem = { id: string; name: string };

export function JDFilter() {
  const nav = useNavigate({ from: "/app/jd" });
  const search = useSearch({ from: "/app/jd" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const statusFilter = findCondition(filters, "status");
  const departmentFilter = findCondition(filters, "departmentId");
  const levelFilter = findCondition(filters, "level");

  const [searchValue, setSearchValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertOrGroup(filters, [
        { field: "title", op: "contains", value: val },
        { field: "departmentName", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "title");
      nextFilters = removeConditions(nextFilters, "departmentName");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? String(statusFilter.value) : "";

  const departmentValue = (departmentFilter?.value as string) || "";

  const levelValue =
    typeof levelFilter?.value === "string" ? String(levelFilter.value) : "";

  const hasFilter =
    !!titleFilter?.value || !!statusValue || !!departmentValue || !!levelValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ JD..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["departments", "filter-jd"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "departmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={levelValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "level");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "level",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ລະດັບ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "Junior", label: "Junior" },
              { value: "Senior", label: "Senior" },
              { value: "Manager", label: "Manager" },
              { value: "Director", label: "Director" },
              { value: "Other", label: "ອື່ນໆ" },
            ]}
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "draft", label: "ຮ່າງ" },
              { value: "published", label: "ໃຊ້ງານ" },
              { value: "archived", label: "ບໍ່ໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
